import { groupBy } from "lodash";
import React, { createContext, FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import MarketExpansionService from "@app/overviews/market-expansion-dashboard/marketExpansionService";
import { useGetMedicaidPrograms } from "@app/overviews/market-expansion-dashboard/hooks/medicaidQueries";
import { MarketExpansionCounty } from "@app/overviews/market-expansion-dashboard/shared/MarketExpansionCounty";
import { MarketExpansionLineOfBusinessEnum } from "@app/overviews/market-expansion-dashboard/shared/MarketExpansionLineOfBusinessEnum";
import { MarketExpansionState } from "@app/overviews/market-expansion-dashboard/shared/MarketExpansionState";
import { MedicaidProgram } from "@app/overviews/market-expansion-dashboard/shared/MedicaidProgram";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { UserContext } from "@app/user/UserContext";

/** Stable empty array so `programOptions` doesn't create a new reference every render while the query has no data, which would otherwise retrigger the default-select-all effect in a loop. */
const EMPTY_PROGRAMS: MedicaidProgram[] = [];

export interface MarketExpansionFiltersContextValues {
  /* oxlint-disable sort-keys */
  clientNotConfiguredError: boolean;
  marketExpansionForbiddenError: boolean;

  selectedLineOfBusiness: MarketExpansionLineOfBusinessEnum | null;
  setSelectedLineOfBusiness: (value: MarketExpansionLineOfBusinessEnum) => void;
  linesOfBusiness: MarketExpansionLineOfBusinessEnum[];
  linesOfBusinessLoading: boolean;

  selectedState: string | null;
  setSelectedState: (value: string) => void;
  stateOptions: MarketExpansionState[];
  selectedStateAbbreviation: string | undefined;
  stateNotFoundError: boolean;
  setStateNotFoundError: (value: boolean) => void;

  selectedCounties: string[];
  setSelectedCounties: (value: string[]) => void;
  countyOptions: MarketExpansionCounty[];

  selectedPrograms: string[];
  setSelectedPrograms: (value: string[]) => void;
  programOptions: MedicaidProgram[];
  programsError: boolean;
  programsLoading: boolean;
  programSelectionReady: boolean;

  stateCountyFilterOptionsLoading: boolean;
  medicareMarketOpportunityHistoricalDataCached: boolean;

  resetFilters: () => void,

  allCountiesSelected: boolean;
  allProgramsSelected: boolean;

  getCBSAGroupedDropdownItems: (counties: MarketExpansionCounty[]) => NestingParentDropdownItem[];
  /* oxlint-enable sort-keys */
}

const initialValues: MarketExpansionFiltersContextValues = {
  /* oxlint-disable sort-keys */
  clientNotConfiguredError: false,
  marketExpansionForbiddenError: false,

  selectedLineOfBusiness: null,
  setSelectedLineOfBusiness: () => null,
  linesOfBusiness: [],
  linesOfBusinessLoading: false,

  selectedState: null,
  setSelectedState: () => null,
  stateOptions: [],
  selectedStateAbbreviation: undefined,
  stateNotFoundError: false,
  setStateNotFoundError: () => null,

  selectedCounties: [],
  setSelectedCounties: () => null,
  countyOptions: [],

  selectedPrograms: [],
  setSelectedPrograms: () => null,
  programOptions: [],
  programsError: false,
  programsLoading: false,
  programSelectionReady: true,

  stateCountyFilterOptionsLoading: false,
  medicareMarketOpportunityHistoricalDataCached: false,

  resetFilters: () => null,

  allCountiesSelected: false,
  allProgramsSelected: false,

  getCBSAGroupedDropdownItems: () => [],
  /* oxlint-enable sort-keys */
}

export const MarketExpansionFiltersContext = createContext(initialValues);

const MarketExpansionFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {

  const [clientNotConfiguredError, setClientNotConfiguredError] = useState<boolean>(initialValues.clientNotConfiguredError);
  const [marketExpansionForbiddenError, setMarketExpansionForbiddenError] = useState<boolean>(initialValues.marketExpansionForbiddenError);

  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<MarketExpansionLineOfBusinessEnum | null>(initialValues.selectedLineOfBusiness);
  const [linesOfBusiness, setLinesOfBusiness] = useState<MarketExpansionLineOfBusinessEnum[]>(initialValues.linesOfBusiness);
  const [linesOfBusinessLoading, setLinesOfBusinessLoading] = useState<boolean>(initialValues.linesOfBusinessLoading);

  const [stateOptions, setStateOptions] = useState<MarketExpansionState[]>(initialValues.stateOptions);
  const [selectedState, setSelectedState] = useState<string | null>(initialValues.selectedState);
  const [stateNotFoundError, setStateNotFoundError] = useState<boolean>(initialValues.stateNotFoundError);

  const [selectedCounties, setSelectedCounties] = useState<string[]>(initialValues.selectedCounties);

  const [selectedPrograms, setSelectedPrograms] = useState<string[]>(initialValues.selectedPrograms);

  const [stateCountyFilterOptionsLoading, setStateCountyFilterOptionsLoading] = useState(false);
  const [medicareMarketOpportunityHistoricalDataCached, setMedicareMarketOpportunityHistoricalDataCached] = useState(false);

  const userContext = useContext(UserContext);
  const marketExpansionService = useRef(new MarketExpansionService()).current;

  const selectedStateOption = selectedState ? stateOptions.find(x => x.stateName === selectedState) : undefined;

  const countyOptions = useMemo(() => selectedStateOption?.counties ?? [], [selectedStateOption]);
  const selectedStateAbbreviation = useMemo(() => selectedStateOption?.stateAbbreviation, [selectedStateOption]);

  const allCountiesSelected = countyOptions.length > 0 && selectedCounties.length === countyOptions.length;

  const isMedicaid = selectedLineOfBusiness === MarketExpansionLineOfBusinessEnum.Medicaid;
  const programsQuery = useGetMedicaidPrograms(isMedicaid ? selectedStateAbbreviation : undefined);
  const programOptions = programsQuery.data ?? EMPTY_PROGRAMS;
  const programsLoading = isMedicaid && programsQuery.isPending;
  const programsError = isMedicaid && programsQuery.isError;
  const programSelectionReady = !isMedicaid || (!programsLoading && !programsError);

  const allProgramsSelected = programOptions.length > 0 && selectedPrograms.length === programOptions.length;

  const getCBSAGroupedDropdownItems = (counties: MarketExpansionCounty[]): NestingParentDropdownItem[] => {
    const result: NestingParentDropdownItem[] = [];

    const grouped = groupBy(counties.filter(c => !!c.countyCBSACode), c => c.countyCBSACode);
    Object.entries(grouped).forEach(([cbsaCode, countiesInGroup]) => {
      result.push({
        checkboxStatus: CheckboxStateEnum.Unchecked,
        children: countiesInGroup.map(c => ({ key: c.countySSA, name: c.countyName })),
        grandparentTitle: "Statistical Area",
        key: cbsaCode,
        name: countiesInGroup[0].countyCBSAName,
      });
    });

    result.push({
      checkboxStatus: CheckboxStateEnum.Unchecked,
      children: counties.map(c => ({ key: c.countySSA, name: c.countyName })),
      grandparentTitle: "County",
      hideParent: true,
      key: "county",
      name: "",
    });

    return result;
  };

  useEffect(() => {
    setLinesOfBusinessLoading(true);

    marketExpansionService.getMarketExpansionLinesOfBusiness(userContext.clientId).then((data) => {
      if (!!data) {
        if (!data.length) {
          setClientNotConfiguredError(true);
        } else {
          setLinesOfBusiness(data);
          if (data.length === 1) {
            setSelectedLineOfBusiness(data[0]);
          }
        }
        setLinesOfBusinessLoading(false);
      }
    }).catch((error) => {
      if (error?.status === 403) {
        setMarketExpansionForbiddenError(true);
      } else {
        setSelectedLineOfBusiness(null);
      }
      setLinesOfBusinessLoading(false);
    });
  }, []);

  useEffect(() => {
    setSelectedState(initialValues.selectedState);
    setSelectedCounties(initialValues.selectedCounties);
    setSelectedPrograms(initialValues.selectedPrograms);

    if (!selectedLineOfBusiness) {
      setStateCountyFilterOptionsLoading(false);
      return;
    }

    setStateCountyFilterOptionsLoading(true);

    // Guards against a stale response from a superseded line-of-business selection overwriting state after the fact
    let isCurrent = true;

    marketExpansionService.getMarketExpansionStateCountyFilters(userContext.clientId, selectedLineOfBusiness).then((data) => {
      if (!isCurrent) return;
      if (!!data) {
        setStateOptions(data.serviceArea);
      }
      setStateCountyFilterOptionsLoading(false);
    });

    return () => {
      isCurrent = false;
    };
  }, [selectedLineOfBusiness]);

  useEffect(() => {
    if (!medicareMarketOpportunityHistoricalDataCached) {
      marketExpansionService.cacheAllMedicareMarketOpportunityHistoricalData().then((result) => {
        if (result) {
          setMedicareMarketOpportunityHistoricalDataCached(true);
        }
      }).catch(() => setMedicareMarketOpportunityHistoricalDataCached(false));
    }
  }, []);

  useEffect(() => {
    setStateNotFoundError(false);

    const countySSAs = countyOptions.map(c => c.countySSA);
    setSelectedCounties(countySSAs);
  }, [countyOptions]);

  useEffect(() => {
    if (isMedicaid && (programsLoading || programsError)) {
      return;
    }

    // The Programs query has background revalidation disabled (see useGetMedicaidPrograms) since
    // the underlying data only changes quarterly, so this only ever fires for a genuinely new
    // state (or the initial load) - always defaulting to all selected is correct either way.
    setSelectedPrograms(programOptions.map(p => p.programKey));
  }, [programOptions, programsLoading, programsError]);

  const resetFilters = () => {
    setSelectedLineOfBusiness(linesOfBusiness.length === 1 ? linesOfBusiness[0] : initialValues.selectedLineOfBusiness);
    setSelectedState(initialValues.selectedState);
    setSelectedCounties(initialValues.selectedCounties);
    setSelectedPrograms(initialValues.selectedPrograms);
  }

  return (
    <MarketExpansionFiltersContext.Provider
      value={{
        /* oxlint-disable sort-keys */
        clientNotConfiguredError,
        marketExpansionForbiddenError,

        selectedLineOfBusiness,
        setSelectedLineOfBusiness,
        linesOfBusiness,
        linesOfBusinessLoading,

        selectedState,
        setSelectedState,
        stateOptions,
        selectedStateAbbreviation,
        stateNotFoundError,
        setStateNotFoundError,

        selectedCounties,
        setSelectedCounties,
        countyOptions,

        selectedPrograms,
        setSelectedPrograms,
        programOptions,
        programsError,
        programsLoading,
        programSelectionReady,

        stateCountyFilterOptionsLoading,
        medicareMarketOpportunityHistoricalDataCached,

        resetFilters,

        allCountiesSelected,
        allProgramsSelected,

        getCBSAGroupedDropdownItems,
        /* oxlint-enable sort-keys */
      }}>
      {props.children}
    </MarketExpansionFiltersContext.Provider>
  )
}

export default MarketExpansionFiltersContextProvider;